import { useEffect, useRef, useState } from "react";
import {
  BookOpen,
  FileText,
  Loader2,
  MessageSquareText,
  Plus,
  Search,
  Settings,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import ChatPane from "@/components/ChatPane";
import ConversationHistoryPane from "@/components/ConversationHistoryPane";
import DocumentViewer from "@/components/DocumentViewer";
import QararkJordanWarning from "@/components/QararkJordanWarning";
import QararkSettings from "@/components/QararkSettings";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  createResearchSession,
  deleteResearchSession,
} from "@/lib/api";
import {
  invalidateDocuments,
  invalidateResearchSessions,
} from "@/lib/cache/invalidate";
import { queryClient } from "@/lib/cache/client";
import { queryKeys } from "@/lib/cache/queryKeys";
import {
  useQararkStatus,
  useResearchSession,
  useResearchSessions,
} from "@/hooks/queries";
import { useLang } from "@/lib/lang";

function groupsFor(documents) {
  return (documents || []).reduce((groups, document) => {
    const key = document.source_query || "";
    if (!groups[key]) groups[key] = [];
    groups[key].push(document);
    return groups;
  }, {});
}

export default function ResearchPane({ model }) {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** True while composing a blank research session (not tied to history). */
  const [isComposingNew, setIsComposingNew] = useState(false);
  const didAutoSelect = useRef(false);

  const { data: status = { connected: false } } = useQararkStatus();
  const { data: sessions = [], isLoading: sessionsLoading } =
    useResearchSessions();
  const { data: activeSession, isLoading: sessionLoading } =
    useResearchSession(activeSessionId);

  useEffect(() => {
    if (didAutoSelect.current || isComposingNew || sessionsLoading) return;
    if (!activeSessionId && sessions.length > 0) {
      setActiveSessionId(sessions[0].id);
      didAutoSelect.current = true;
    }
  }, [activeSessionId, isComposingNew, sessions, sessionsLoading]);

  useEffect(() => {
    if (!activeSessionId) {
      setSelectedDocumentId(null);
      setConversationId(null);
      return;
    }
    setSelectedDocumentId(activeSession?.documents?.[0]?.id || null);
    setConversationId(null);
  }, [activeSessionId, activeSession?.id, activeSession?.documents]);

  const beginNewSession = () => {
    setIsComposingNew(true);
    setActiveSessionId(null);
    setSelectedDocumentId(null);
    setConversationId(null);
    setQuery("");
  };

  const selectSession = (sessionId) => {
    setIsComposingNew(false);
    setActiveSessionId(sessionId);
  };

  const startResearch = async () => {
    const text = query.trim();
    if (!text || creating) return;
    if (!status.connected) {
      setSettingsOpen(true);
      toast.error(t("research_connect_required"));
      return;
    }
    setCreating(true);
    try {
      const created = await createResearchSession(text, model);
      queryClient.setQueryData(
        queryKeys.researchSessions.detail(created.id),
        created,
      );
      await Promise.all([
        invalidateResearchSessions(),
        invalidateDocuments(),
      ]);
      setIsComposingNew(false);
      setActiveSessionId(created.id);
      setSelectedDocumentId(created.documents?.[0]?.id || null);
      setConversationId(null);
      setQuery("");
      if (!created.documents?.length) {
        toast.error(t("research_no_results"));
      }
    } catch (error) {
      if (error?.response?.status === 428) setSettingsOpen(true);
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setCreating(false);
    }
  };

  const removeSession = async (event, id) => {
    event.stopPropagation();
    if (!window.confirm(t("research_delete_confirm"))) return;
    try {
      await deleteResearchSession(id);
      if (activeSessionId === id) {
        beginNewSession();
      }
      await invalidateResearchSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  const documentGroups = groupsFor(activeSession?.documents);

  return (
    <div
      className="grid h-full min-h-0 grid-cols-1 overflow-y-auto md:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(300px,0.85fr)_minmax(420px,1.15fr)] xl:overflow-hidden bg-white"
      data-testid="research-pane"
    >
      <aside className="hidden md:flex md:row-span-2 xl:row-span-1 min-h-0 flex-col grid-border-e bg-slate-50/60">
        <div className="flex items-center justify-between grid-border-b px-4 py-3">
          <span className="eyebrow !text-[10px]">{t("research_history")}</span>
          <button
            type="button"
            data-testid="research-new-session"
            onClick={beginNewSession}
            className={[
              "p-1 transition-colors",
              isComposingNew
                ? "text-slate-900"
                : "text-slate-500 hover:text-slate-900",
            ].join(" ")}
            aria-label={t("research_new")}
          >
            <Plus size={15} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {sessionsLoading && (
            <Loader2 size={16} className="mx-auto my-8 animate-spin text-slate-400" />
          )}
          {!sessionsLoading && sessions.length === 0 && (
            <p className="px-4 py-8 text-center text-xs text-slate-400">
              {t("research_no_sessions")}
            </p>
          )}
          {sessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => selectSession(session.id)}
              className={[
                "group w-full grid-border-b px-4 py-3 text-start transition-colors",
                !isComposingNew && session.id === activeSessionId
                  ? "bg-slate-900 text-white"
                  : "hover:bg-white",
              ].join(" ")}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">
                    {session.original_query}
                  </p>
                  <p
                    className={[
                      "mt-1 text-[10px]",
                      !isComposingNew && session.id === activeSessionId
                        ? "text-white/60"
                        : "text-slate-400",
                    ].join(" ")}
                  >
                    {t("research_source_count").replace(
                      "{n}",
                      String(session.doc_count || 0),
                    )}
                  </p>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => removeSession(event, session.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") removeSession(event, session.id);
                  }}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label={t("delete")}
                >
                  <Trash2 size={12} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="flex min-h-[620px] flex-col grid-border-e xl:min-h-0">
        <div className="grid-border-b p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="eyebrow mb-1">{t("nav_research")}</p>
              <h1 className="font-serif-display text-xl text-slate-900">
                {t("research_title")}
              </h1>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSettingsOpen(true)}
              className="rounded-none"
            >
              <Settings size={14} />
              {status.connected
                ? t("qarark_connected")
                : t("qarark_connect")}
            </Button>
          </div>
          <QararkJordanWarning className="mb-3" />
          <Textarea
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                startResearch();
              }
            }}
            placeholder={t("research_query_placeholder")}
            className="min-h-[84px] resize-none rounded-none"
          />
          <Button
            type="button"
            onClick={startResearch}
            disabled={creating || !query.trim()}
            className="mt-2 w-full rounded-none"
          >
            {creating ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Search size={15} />
            )}
            {creating ? t("research_searching") : t("research_start")}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sessionLoading && (
            <Loader2 size={18} className="mx-auto my-12 animate-spin text-slate-400" />
          )}
          {!sessionLoading && !activeSession && (
            <div className="px-7 py-14 text-center">
              <BookOpen size={28} className="mx-auto mb-4 text-slate-300" />
              <h2 className="font-serif-display text-xl text-slate-900">
                {t("research_empty_title")}
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                {t("research_empty_hint")}
              </p>
            </div>
          )}
          {activeSession && (
            <>
              <div className="grid-border-b px-4 py-3">
                <p className="text-sm font-medium text-slate-900">
                  {activeSession.original_query}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(activeSession.suggested_queries || []).map((spec, index) => (
                    <span
                      key={`${spec.query}-${index}`}
                      className="border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-600"
                    >
                      {spec.query} · {spec.content_type}
                    </span>
                  ))}
                </div>
              </div>
              {Object.entries(documentGroups).map(([sourceQuery, documents]) => (
                <div key={sourceQuery}>
                  <div className="grid-border-b bg-slate-50 px-4 py-2">
                    <span className="text-[10px] font-mono-tight uppercase tracking-wider text-slate-500">
                      {sourceQuery}
                    </span>
                  </div>
                  {documents.map((document) => (
                    <button
                      key={document.id}
                      type="button"
                      onClick={() => setSelectedDocumentId(document.id)}
                      className={[
                        "w-full grid-border-b px-4 py-3 text-start transition-colors",
                        document.id === selectedDocumentId
                          ? "bg-slate-900 text-white"
                          : "hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <div className="flex gap-3">
                        <FileText size={15} className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium">
                            {document.filename?.replace(/^qarark-/, "").replace(/\.txt$/, "")}
                          </p>
                          <p
                            className={[
                              "mt-1 line-clamp-2 text-[10px] leading-relaxed",
                              document.id === selectedDocumentId
                                ? "text-white/65"
                                : "text-slate-500",
                            ].join(" ")}
                          >
                            {document.preview}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))}
              {activeSession.documents?.length === 0 && (
                <p className="px-5 py-10 text-center text-sm text-slate-500">
                  {t("research_no_results")}
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <section className="flex min-h-[760px] flex-col md:col-start-2 xl:col-start-auto xl:min-h-0">
        <div className="flex min-h-0 flex-[1.05] flex-col grid-border-b">
          <div className="flex items-center gap-2 grid-border-b bg-slate-50 px-4 py-2">
            <BookOpen size={13} />
            <span className="eyebrow !text-[10px]">{t("research_reader")}</span>
          </div>
          <div className="min-h-0 flex-1">
            <DocumentViewer documentId={selectedDocumentId} />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-2 grid-border-b bg-slate-50 px-4 py-2">
            <MessageSquareText size={13} />
            <span className="eyebrow !text-[10px]">{t("research_chat")}</span>
          </div>
          {activeSessionId ? (
            <div className="flex min-h-0 flex-1">
              <aside className="hidden 2xl:block w-[180px] flex-shrink-0 grid-border-e">
                <ConversationHistoryPane
                  researchSessionId={activeSessionId}
                  activeConversationId={conversationId}
                  onSelectConversation={(conversation) =>
                    setConversationId(conversation.id)
                  }
                  onNewConversation={() => setConversationId(null)}
                />
              </aside>
              <div className="min-w-0 flex-1">
                <ChatPane
                  documents={activeSession?.documents || []}
                  researchSessionId={activeSessionId}
                  hideContextPicker
                  allowEmptyContext
                  model={model}
                  conversationId={conversationId}
                  onConversationCreated={setConversationId}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-slate-400">
              {t("research_chat_empty")}
            </div>
          )}
        </div>
      </section>

      <QararkSettings
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </div>
  );
}
